import Button from "@/components/Button"
import { ReactComponent as BinIcon } from "@/icons/bin-icon.svg"
import { ReactComponent as ExportIcon } from "@/icons/export-icon.svg"
import { ReactComponent as StarIcon } from "@/icons/star-icon.svg"
import type { Preset } from "@/hooks/presets"
import cx from "classnames"
import { useSerialDevice } from "@/contexts/serial-device"
import { DEVICE_CONNECTED } from "@/constants"
import { useAuth } from "@/contexts/auth"
import { ReactComponent as InfoIcon } from "@/icons/info-icon.svg"
import { ReactComponent as CheckIcon } from "@/icons/strong-check-icon.svg"
import { ReactComponent as ArrowDownIcon } from "@/icons/arrow-down-icon.svg"
import { ReactComponent as ResetIcon } from "@/icons/reset-icon.svg"
import { Select as NextUISelect, SelectItem, Tooltip } from "@nextui-org/react"
import { useState } from "react"
import ExportOptionsModal from "@/components/modals/ExportOptionsModal"

export type AutoSaveMode = "disabled" | "immediate" | "10s" | "1min"

type Props = {
  activePreset: Preset
  activePresetOutdated: boolean
  onDisableEqClicked: () => void
  onDeletePresetClicked: (preset: Preset) => void
  onExportClicked: () => void
  onFavouritePresetClicked: (preset: Preset) => void
  onSavePresetClicked: (preset: Preset) => void
  onSaveNewPresetClicked: (preset: Preset) => void
  onGetLinkClicked: () => void
  onExportAllClicked: () => void
  onImportPresetClicked: () => void
  autoSave?: AutoSaveMode
  onAutoSaveChange: (value: AutoSaveMode) => void
}

const PresetHeader = ({
  activePreset,
  activePresetOutdated,
  onDisableEqClicked,
  onDeletePresetClicked,
  onExportClicked,
  onFavouritePresetClicked,
  onSavePresetClicked,
  onSaveNewPresetClicked,
  onGetLinkClicked,
  onExportAllClicked,
  onImportPresetClicked,
  autoSave = "immediate",
  onAutoSaveChange,
}: Props) => {
  const { user } = useAuth()
  const serialDevice = useSerialDevice()
  const isDeviceConnected = serialDevice.status === DEVICE_CONNECTED
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  const autoSaveOptions = [
    { value: "immediate", label: "Save immediately" },
    { value: "disabled", label: "Disabled" },
    { value: "10s", label: "Save after 10 seconds" },
    { value: "1min", label: "Save after 1 minute" },
  ]

  return (
    <div className="flex items-center justify-between">
      {activePreset ? (
        <div className="flex flex-col">
          <div className="type-heading-sm -mb-2px inline-flex items-center font-bold">
            {activePreset.name}
            <button
              title={"Make this preset a favourite"}
              className="-mt-1 ml-4 cursor-pointer"
              onClick={() => onFavouritePresetClicked(activePreset)}
            >
              <StarIcon
                className={cx(
                  "-mt-px h-4 w-4",
                  activePreset.is_favourite
                    ? "[&>path]:fill-yellow [&>path]:stroke-none"
                    : "[&>path]:fill-none [&>path]:stroke-gray-300"
                )}
              />
            </button>
          </div>
          {(activePreset.author || activePreset.headphone) && (
            <div className="type-body-xs mt-1 text-silver">
              {activePreset.headphone && (
                <span>
                  {activePreset.headphone.brand.name} {activePreset.headphone.model}
                </span>
              )}
              {activePreset.author && activePreset.headphone && (
                <span className="text-silver"> • </span>
              )}
              {activePreset.author && (
                <>
                  Preset by{" "}
                  {activePreset.attribution_link === "https://github.com/opra-project/OPRA" ? (
                    // For OPRA presets: show author without link, make OPRA clickable
                    <>
                      <span className="font-bold text-silver">{activePreset.author}</span>
                      {activePreset.author === "oratory1990" && (
                        <span className="ml-2">
                          <a
                            href="http://www.buymeacoffee.com/oratory1990"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-xs text-[#FFDD00] transition-colors hover:text-[#FFDD00]/80"
                            title="Support oratory1990's work"
                          >
                            ☕
                          </a>
                        </span>
                      )}
                      <span className="text-silver"> - Distributed by </span>
                      <a
                        href="https://github.com/opra-project/OPRA"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue underline transition-colors hover:text-brightest-blue"
                      >
                        OPRA
                      </a>
                    </>
                  ) : activePreset.attribution_link ? (
                    // For other presets with attribution link: make author clickable
                    <>
                      <a
                        href={activePreset.attribution_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-silver underline transition-colors hover:text-white"
                      >
                        {activePreset.author}
                      </a>
                      {activePreset.author === "oratory1990" && (
                        <span className="ml-2">
                          <a
                            href="http://www.buymeacoffee.com/oratory1990"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-xs text-[#FFDD00] transition-colors hover:text-[#FFDD00]/80"
                            title="Support oratory1990's work"
                          >
                            ☕
                          </a>
                        </span>
                      )}
                    </>
                  ) : (
                    // For presets without attribution link: just show author name
                    <>
                      <span className="font-bold text-silver">{activePreset.author}</span>
                      {activePreset.author === "oratory1990" && (
                        <span className="ml-2">
                          <a
                            href="http://www.buymeacoffee.com/oratory1990"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-xs text-[#FFDD00] transition-colors hover:text-[#FFDD00]/80"
                            title="Support oratory1990's work"
                          >
                            ☕
                          </a>
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="type-heading-base inline-flex items-center font-bold"> </div>
      )}
      <div className="flex items-center gap-x-1.5">
        <Button theme="red" size="xs" onClick={onDisableEqClicked}>
          Clear EQ
          <ResetIcon className="h-3 w-3 text-red" />
        </Button>
        {user && (
          <Button
            theme="blue"
            size="xs"
            title="Create a new preset from current EQ settings"
            onClick={() => onSaveNewPresetClicked(activePreset)}
            disabled={!isDeviceConnected}
          >
            Save As New Preset
          </Button>
        )}
        <Button
          theme="blue"
          size="xs"
          onClick={onImportPresetClicked}
          title="Import a preset from a file"
          disabled={!isDeviceConnected}
        >
          Import
        </Button>
        <Button
          theme="blue"
          size="xs"
          onClick={() => setIsExportModalOpen(true)}
          title="Export the current EQ settings"
          disabled={!isDeviceConnected}
        >
          Export
          <ExportIcon className="h-3 w-3 text-brightest-blue" />
        </Button>
        {activePreset && (
          <div className="flex items-center pl-1">
            <button
              className="clickable h-3 w-3 text-silver"
              title="Delete this preset"
              onClick={() => onDeletePresetClicked(activePreset)}
            >
              <BinIcon />
            </button>
          </div>
        )}
        <div className="ml-3.5 flex items-center gap-x-4 border-l border-mine-shaft pl-5">
          <div className="flex items-center">
            <span className="type-body-xs font-bold text-blue">Auto Save</span>
            <Tooltip
              content={
                "Auto Save: Element IV briefly mutes audio to save changes to flash memory. You may Disable Auto Save or delay Save events to hear EQ changes in real-time. Just remember to manually save your settings when choosing Disabled to prevent loss upon powering down."
              }
              placement="bottom"
              classNames={{
                content:
                  "max-w-[400px] px-5 py-2 bg-red rounded-md type-body-sm bg-tundora text-silver",
              }}
            >
              <span className="ml-1.5 cursor-help">
                <InfoIcon className="h-3 w-3 text-blue" />
              </span>
            </Tooltip>
          </div>
          <NextUISelect
            radius="none"
            size="sm"
            disallowEmptySelection
            selectionMode="single"
            selectedKeys={new Set([autoSave])}
            onSelectionChange={(keys) => {
              const newValue = Array.from(keys)[0] as AutoSaveMode
              console.log("Auto save changed to:", newValue)
              onAutoSaveChange(newValue)
            }}
            classNames={{
              trigger:
                "border border-[#383C4A] data-[hover=true]:border-[#626675] data-[hover=true]:bg-[#272A2F] transition-colors rounded-plus pl-4 bg-[#232528] min-h-7 h-7 shadow-auto-save-dropdown",
              value:
                "text-ghost group-data-[has-value=true]:text-white text-xs font-bulo font-bold",
              popoverContent:
                "bg-[#212121] rounded-plus border border-[#2E3037] p-0 shadow-outside text-ghost",
              selectorIcon: "w-2.5 h-1.5",
            }}
            listboxProps={{
              itemClasses: {
                base: [
                  "!rounded-plus pl-3 py-1.5",
                  "data-[hover=true]:text-ghost",
                  "data-[hover=true]:bg-mine-shaft",
                  "data-[selectable=true]:focus:text-ghost",
                  "data-[selectable=true]:focus:bg-mine-shaft",
                ],
                title: "font-bulo text-xs font-bold",
              },
            }}
            popoverProps={{
              placement: "bottom-start",
              motionProps: {
                variants: {
                  initial: {
                    opacity: 0,
                    transform: "scaleY(0.9)",
                  },
                  enter: {
                    opacity: 1,
                    transform: "scaleY(1)",
                    transition: {
                      type: "spring",
                      bounce: 0,
                      duration: 0.3,
                    },
                  },
                  exit: {
                    opacity: 0,
                    transform: "scaleY(0.96)",
                    transition: {
                      type: "easeOut",
                      bounce: 0,
                      duration: 0.15,
                    },
                  },
                },
              },
            }}
            selectorIcon={<ArrowDownIcon />}
            className="w-40"
            aria-label="Auto save mode"
          >
            {autoSaveOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                hideSelectedIcon={option.value !== autoSave}
                selectedIcon={<CheckIcon className="h-2.5 w-2.5 text-red" />}
              >
                {option.label}
              </SelectItem>
            ))}
          </NextUISelect>
        </div>
      </div>

      <ExportOptionsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExportLink={onGetLinkClicked}
        onExportSingle={onExportClicked}
        onExportAll={onExportAllClicked}
      />
    </div>
  )
}

export default PresetHeader
